/* eslint-disable react/no-multi-comp */
/* eslint-disable no-param-reassign */
import React, { useState, useRef, useEffect } from "react"
import { GridLines, Headline } from "@shared"
import { css, useSassUnitValues } from "@jsUtils"
import { CONSTANTS } from "@data"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { PageSection } from "../PageSection"
import Styles from "./mobileDemo.module.scss"

const { BREAKPOINTS } = CONSTANTS
const MOBILE_BREAKPOINT = useSassUnitValues(BREAKPOINTS.MOBILE)

const videoFilenames = ["1_ghosting", "2_catfishing", "3_perfect", "4_direction"]
const DemoVideo = ({ active, videoName, className }) => {
  const vid = useRef()
  let loopTimeout = null
  // loop video with delay
  const handleEnd = () => {
    setTimeout(() => {
      if (vid.current) vid.current.currentTime = 0
      setTimeout(() => {
        if (vid && active && vid.current) vid.current.play()
      }, 3500)
    }, 2000)
  }

  // stop the video if not in view
  useEffect(() => {
    if (vid) {
      if (active) {
        loopTimeout = setTimeout(() => {
          if (vid.current) vid.current.play()
        }, 2500)
      } else {
        vid.current.pause()
        loopTimeout = setTimeout(() => {
          if (vid.current) vid.current.currentTime = 0
        }, 1000)
      }
    }
  }, [active])

  useEffect(() => {
    return () => {
      clearTimeout(loopTimeout)
    }
  }, [])

  return (
    <video
      ref={vid}
      onEnded={() => handleEnd()}
      muted
      playsInline
      tabIndex={-1}
      className={css(Styles.video, active && Styles.active, className)}
    >
      <source src={`/videos/exo-works/exo_works_demo_${videoName}.mov`} type="video/quicktime" />
      <source src={`/videos/exo-works/exo_works_demo_${videoName}.webm`} type="video/webm" />
    </video>
  )
}

// Each step - full page text block
const ViewBlock = React.forwardRef((props, ref) => {
  const { index, headline, description } = props

  return (
    <div ref={ref} className={css(Styles.textWrapper, index % 2 !== 0 && Styles.right)}>
      <div className={Styles.textGroup}>
        <Headline size={5}>{headline}</Headline>
        <div className={Styles.copy} dangerouslySetInnerHTML={{ __html: description }} />
      </div>
    </div>
  )
})

const MobileDemoSection = props => {
  const { data, theme, ready } = props
  const [mobileLayout, setMobileLayout] = useState(false)
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const refs = useRef([])
  const ref = useRef()
  const [scrollTrigger, setScrollTrigger] = useState()

  useEffect(() => {
    let tl
    const element = ref.current
    if (element) {
      const matchMedia = {}
      matchMedia[`(min-width: ${MOBILE_BREAKPOINT}px)`] = () => {
        tl = new gsap.timeline({
          scrollTrigger: {
            trigger: element,
            pin: true,
            scrub: 1,
            end: `+=${window.innerHeight * 4}`,
            onUpdate: self => {
              setProgress(self.progress)

              if (refs) {
                refs.current.forEach((el, index) => {
                  const activeIndex = Math.floor(self.progress * 4)
                  el.style.opacity = activeIndex === index ? 1 : 0
                })
              }
            }
          }
        })
        setScrollTrigger(tl)
      }
      ScrollTrigger.matchMedia(matchMedia)
    }
    return () => {
      tl?.kill()
      if (element) {
        const parent = element.parentNode
        if (parent.classList.contains("pin-spacer")) {
          const grandparent = parent?.parentNode
          if (grandparent) {
            grandparent.insertBefore(element, parent)
            try {
              parent?.remove()
            } catch {
              console.warn("Failed to Remove Wrapper from Mobile Demo Section")
            }
          }
        }
      }
    }
  }, [ref, refs])

  const onResize = () => {
    if (window.innerWidth < MOBILE_BREAKPOINT && !mobileLayout) {
      setMobileLayout(true)
    } else if (window.innerWidth >= MOBILE_BREAKPOINT && mobileLayout) {
      setMobileLayout(false)
    }
  }

  useEffect(() => {
    onResize()
    window.addEventListener("resize", onResize)
    return () => {
      window.removeEventListener("resize", onResize)
    }
  })

  return (
    <div ref={ref} className={css(Styles.wrapper, mobileLayout && Styles.mobile)}>
      <PageSection theme={theme} className={css(Styles.section, ready && Styles.ready)}>
        <GridLines theme={theme} />
        {data && mobileLayout ? (
          <div>
            {data.map((step, idx) => {
              return (
                <div key={step.id} className={Styles.itemWrapperMobile}>
                  <div className={css(Styles.textGroup, Styles.mobile)}>
                    <Headline size={5}>{step.headline}</Headline>
                    <div
                      className={Styles.copy}
                      dangerouslySetInnerHTML={{
                        __html: step.description?.childMarkdownRemark?.html
                      }}
                    />
                  </div>
                  <div className={css(Styles.videoWrapper, Styles.mobile)}>
                    <div className={css(Styles.videoContainer, Styles.mobile)}>
                      <DemoVideo videoName={videoFilenames[idx]} active className={Styles.mobile} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <>
            <div className={Styles.videoWrapper}>
              <div className={Styles.videoContainer}>
                {videoFilenames.map((vidName, idx) => {
                  const active =
                    idx === 3 ? Math.floor(progress * 4) >= 3 : Math.floor(progress * 4) === idx
                  return <DemoVideo key={vidName} videoName={vidName} active={active} />
                })}
              </div>
            </div>
            <div ref={ref}>
              {data.map((step, idx) => {
                return (
                  <ViewBlock
                    ref={el => {
                      refs.current[idx] = el
                    }}
                    key={step.id}
                    index={idx}
                    headline={step.headline}
                    description={step.description?.childMarkdownRemark?.html}
                  />
                )
              })}
            </div>
          </>
        )}
      </PageSection>
    </div>
  )
}

export default MobileDemoSection
